const DEFAULT_DISPLAY_FORMAT = {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
};

const TIME_ONLY_REGEX = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;
const TIME_ONLY_AMPM_REGEX = /^(0?[1-9]|1[0-2]):([0-5]\d)(?::([0-5]\d))?\s*([AaPp][Mm])$/;
const ISO_WITHOUT_TIMEZONE_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/;
const SPACE_DATETIME_WITHOUT_TIMEZONE_REGEX =
  /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/;

const normalizeTimeOnlyTo24h = (timeValue) => {
  const trimmedValue = String(timeValue || "").trim();

  const twentyFourHourMatch = trimmedValue.match(TIME_ONLY_REGEX);
  if (twentyFourHourMatch) {
    return {
      hours: Number(twentyFourHourMatch[1]),
      minutes: Number(twentyFourHourMatch[2]),
      seconds: Number(twentyFourHourMatch[3] || 0),
    };
  }

  const amPmMatch = trimmedValue.match(TIME_ONLY_AMPM_REGEX);
  if (!amPmMatch) return null;

  let hours = Number(amPmMatch[1]) % 12;
  if (amPmMatch[4].toLowerCase() === "pm") hours += 12;

  return {
    hours,
    minutes: Number(amPmMatch[2]),
    seconds: Number(amPmMatch[3] || 0),
  };
};

const buildDateFromTimeOnly = (timeValue) => {
  const normalizedTime = normalizeTimeOnlyTo24h(timeValue);
  if (!normalizedTime) return null;

  const now = new Date();
  now.setHours(normalizedTime.hours, normalizedTime.minutes, normalizedTime.seconds, 0);
  return now;
};

const normalizeLocalInput = (value) => {
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  if (typeof value !== "string") return null;
  const trimmedValue = value.trim();
  if (!trimmedValue) return null;

  const timeOnlyDate = buildDateFromTimeOnly(trimmedValue);
  if (timeOnlyDate) return timeOnlyDate;

  // Support common local datetime strings like "2026-04-26 22:00:00"
  const normalized = trimmedValue.includes(" ")
    ? trimmedValue.replace(" ", "T")
    : trimmedValue;
  return new Date(normalized);
};

export const convertLocalDateTimeToUTC = (localDateTime) => {
  const parsedDate = normalizeLocalInput(localDateTime);
  if (!parsedDate || Number.isNaN(parsedDate.getTime())) return null;

  return parsedDate.toISOString();
};

export const convertUTCToLocalDateTime = (
  utcDateTime,
  {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale = "en-GB",
    formatOptions = DEFAULT_DISPLAY_FORMAT,
  } = {}
) => {
  if (!utcDateTime) return "—";

  let normalizedUTCInput = utcDateTime;
  const trimmedUTCInput =
    typeof utcDateTime === "string" ? utcDateTime.trim() : utcDateTime;

  // Support UTC time-only payloads like "17:00" by attaching a UTC date.
  if (typeof trimmedUTCInput === "string") {
    const normalizedTime = normalizeTimeOnlyTo24h(trimmedUTCInput);
    if (normalizedTime) {
      const paddedHours = String(normalizedTime.hours).padStart(2, "0");
      const paddedMinutes = String(normalizedTime.minutes).padStart(2, "0");
      const paddedSeconds = String(normalizedTime.seconds).padStart(2, "0");
      normalizedUTCInput = `1970-01-01T${paddedHours}:${paddedMinutes}:${paddedSeconds}.000Z`;
    }
  }

  // Support datetime strings missing timezone by treating them as UTC.
  if (
    typeof normalizedUTCInput === "string" &&
    ISO_WITHOUT_TIMEZONE_REGEX.test(normalizedUTCInput.trim())
  ) {
    normalizedUTCInput = `${normalizedUTCInput.trim()}Z`;
  }

  // Support "YYYY-MM-DD HH:mm:ss" inputs by treating them as UTC too.
  if (
    typeof normalizedUTCInput === "string" &&
    SPACE_DATETIME_WITHOUT_TIMEZONE_REGEX.test(normalizedUTCInput.trim())
  ) {
    const normalizedWithT = normalizedUTCInput.trim().replace(" ", "T");
    normalizedUTCInput = `${normalizedWithT}Z`;
  }

  const parsedDate = new Date(normalizedUTCInput);
  if (Number.isNaN(parsedDate.getTime())) return "—";

  return new Intl.DateTimeFormat(locale, {
    ...formatOptions,
    timeZone: timezone,
  }).format(parsedDate);
};
